/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as markerjs2 from 'markerjs2';
import './Annotation.css';
import { isEmpty } from 'lodash';
import { imageUrl } from '../../helper';

function Annotation({ sampleImage, sourceImage, markerObj, onSaveAnnotation, setIsEditOpen, openType }) {
  const parentRef = useRef();
  const sampleImgRef = useRef();
  const sourceImgRef = useRef();

  const [existingState, setExistingState] = useState({});
  const [imgOpenType, setImgOpenType] = useState('');
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [imagesReady, setImagesReady] = useState(false);
  const [convertedSourceImage, setConvertedSourceImage] = useState(null);
  const [convertedSampleImage, setConvertedSampleImage] = useState(null);
  const existingStateRef = useRef({});
  const isInitializingRef = useRef(false);
  const showMarkerAreaRef = useRef(null);

  /**
   * Checks if a URL is an S3 presigned URL
   * @param {string} url - URL to check
   * @returns {boolean} True if URL is an S3 presigned URL
   */
  const isS3PresignedUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('s3.eu-west-2.amazonaws.com') || (url.includes('s3.') && url.includes('amazonaws.com'));
  }, []);

  const showMarkerArea = useCallback(
    async (action) => {
      if (!imagesReady) {
        return;
      }

      setIsEditOpen(true);
      if (action === 'edit') {
        setImgOpenType(action);
      }

      if (sourceImgRef.current !== null && parentRef.current != null) {
        let stateToRestore = null;
        if (existingStateRef.current && Object.keys(existingStateRef.current).length > 0) {
          stateToRestore = existingStateRef.current;
        } else if (markerObj && Object.keys(markerObj).length > 0) {
          stateToRestore = markerObj;
        }

        const sourceImg = sourceImgRef.current;

        if (!sourceImg.complete || sourceImg.naturalWidth === 0 || sourceImg.naturalHeight === 0) {
          await new Promise((resolve) => {
            const handleLoad = () => {
              sourceImg.removeEventListener('load', handleLoad);
              sourceImg.removeEventListener('error', handleLoad);
              resolve();
            };
            sourceImg.addEventListener('load', handleLoad);
            sourceImg.addEventListener('error', handleLoad);
          });
        }
        const markerArea = new markerjs2.MarkerArea(sourceImgRef.current);
        markerjs2.Activator.addKey('MJS2-F223-S728-5596');

        const currentConvertedSourceImage = convertedSourceImage;
        const currentSourceImage = sourceImage;

        markerArea.settings.defaultColorSet = ['#01ac8c', '#fbb755', '#f65158', 'Blue', '#a5a5a5', '#777777', '#000000', '#FFFFFF'];
        markerArea.settings.defaultColor = '#000000';
        markerArea.settings.defaultFillColor = '#01ac8c';
        markerArea.settings.defaultHighlightColor = '#01ac8c';
        markerArea.settings.defaultColorsFollowCurrentColors = true;
        markerArea.availableMarkerTypes = markerArea.ALL_MARKER_TYPES;

        markerArea.uiStyleSettings.toolboxPanelRowStyleColorsClassName = 'bg-panel';
        markerArea.uiStyleSettings.toolbarStyleColorsClassName = 'custom-toolbar-panel';
        markerArea.uiStyleSettings.toolbarButtonStyleColorsClassName = 'custom-tool-btn';
        markerArea.uiStyleSettings.toolbarOverflowBlockStyleColorsClassName = 'custom-toolbar-overflow';
        markerArea.uiStyleSettings.toolbarActiveButtonStyleColorsClassName = 'custom-tool-btn-active';

        markerArea.uiStyleSettings.toolboxStyleColorsClassName = 'custom-toolbox-panel';
        markerArea.uiStyleSettings.toolboxButtonRowStyleColorsClassName = 'custom-toolbox-button';
        markerArea.uiStyleSettings.toolboxActiveButtonStyleColorsClassName = 'custom-toolbox-button-active';
        markerArea.uiStyleSettings.toolboxButtonStyleColorsClassName = 'custom-toolbox-btn';
        markerArea.uiStyleSettings.toolboxPanelRowStyleColorsClassName = 'custom-toolbox-panel-row';
        markerArea.uiStyleSettings.redoButtonVisible = true;
        markerArea.uiStyleSettings.clearButtonVisible = true;
        markerArea.targetRoot = parentRef.current;
        markerjs2.Activator.addKey('MJS2-F223-S728-5596');

        markerArea.addEventListener('close', () => {
          setIsEditOpen(false);
        });

        markerArea.addEventListener('render', (event) => {
          setIsEditOpen(false);

          try {
            if (sampleImgRef.current) {
              if (event.dataUrl) {
                sampleImgRef.current.src = event.dataUrl;
                const updatedUrl = sampleImgRef.current.src;
                setExistingState(event.state);
                onSaveAnnotation(event.state, existingState, updatedUrl);
              } else {
                console.error('CORS error: Unable to export canvas. Using fallback image.');
                const fallbackUrl =
                  currentConvertedSourceImage ?? (isS3PresignedUrl(currentSourceImage) ? currentSourceImage : `${imageUrl}/${currentSourceImage}`);
                sampleImgRef.current.src = fallbackUrl;
                setExistingState(event.state);
                onSaveAnnotation(event.state, existingState, fallbackUrl);
              }
            }
          } catch (error) {
            console.error('Error in markerArea render event:', error);
            if (sampleImgRef.current) {
              const fallbackUrl =
                currentConvertedSourceImage ?? (isS3PresignedUrl(currentSourceImage) ? currentSourceImage : `${imageUrl}/${currentSourceImage}`);
              sampleImgRef.current.src = fallbackUrl;
              setExistingState(event.state);
              onSaveAnnotation(event.state, existingState, fallbackUrl);
            }
          }
        });

        setTimeout(async () => {
          await markerArea.show();
          if (stateToRestore && Object.keys(stateToRestore).length > 0 && stateToRestore?.markers?.length > 0) {
            setTimeout(() => {
              markerArea.restoreState(stateToRestore);
              if (imgOpenType === 'replace') {
                markerArea.renderState(stateToRestore);
              }
            }, 1000);
          }
        }, 800);
      }
    },
    [convertedSourceImage, sourceImage, imgOpenType, isS3PresignedUrl, setIsEditOpen, onSaveAnnotation, existingState, imagesReady, markerObj],
  );

  useEffect(() => {
    existingStateRef.current = existingState;
    showMarkerAreaRef.current = showMarkerArea;
  }, [existingState, showMarkerArea]);

  const markerObjStr = useMemo(() => JSON.stringify(markerObj), [markerObj]);

  useEffect(() => {
    if (isEmpty(markerObj)) {
      isInitializingRef.current = true;
      setExistingState({});
      existingStateRef.current = {};
      if (imagesReady && showMarkerAreaRef.current) {
        setTimeout(() => {
          if (showMarkerAreaRef.current) {
            showMarkerAreaRef.current('new-edit');
            isInitializingRef.current = false;
          }
        }, 0);
      }
    } else {
      const currentStateStr = JSON.stringify(existingStateRef.current);
      if (markerObjStr !== currentStateStr) {
        setExistingState(markerObj);
        existingStateRef.current = markerObj;
      }
    }
  }, [markerObj, markerObjStr, imagesReady]);

  useEffect(() => {
    setImgOpenType(openType);
  }, [openType]);

  useEffect(() => {
    const stateHasMarkers = existingState?.markers?.length > 0;
    const stateIsNotEmpty = !isEmpty(existingState) && Object.keys(existingState).length > 0;

    if (!isInitializingRef.current && stateIsNotEmpty && stateHasMarkers && imagesReady) {
      const timeoutId = setTimeout(() => {
        if (showMarkerAreaRef.current && !isInitializingRef.current && imagesReady) {
          showMarkerAreaRef.current('');
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [existingState, imagesReady]);

  useEffect(() => {
    return () => {
      setExistingState({});
      setImgOpenType('');
    };
  }, []);

  const getImageSrc = useCallback(
    (image) => {
      if (!image) return '';
      if (isS3PresignedUrl(image)) {
        return image;
      }
      return `${imageUrl}/${image}`;
    },
    [isS3PresignedUrl],
  );

  /**
   * Converts S3 presigned URL to base64 to avoid CORS issues with canvas
   * @param {string} s3ImageUrl - S3 presigned URL
   * @returns {Promise<string>} Base64 string of the image
   */
  const getS3ImageAsBase64 = async (s3ImageUrl) => {
    try {
      const response = await fetch(s3ImageUrl, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error fetching image from S3:', error);
      throw error;
    }
  };

  useEffect(() => {
    const convertImages = async () => {
      if (!sampleImage || !sourceImage) {
        setConvertedSourceImage(null);
        setConvertedSampleImage(null);
        setImagesReady(false);
        return;
      }

      setImagesReady(false);

      try {
        if (isS3PresignedUrl(sourceImage)) {
          try {
            const base64Source = await getS3ImageAsBase64(sourceImage);
            setConvertedSourceImage(base64Source);
          } catch (error) {
            console.error('Failed to convert sourceImage from S3:', error);
            setConvertedSourceImage(sourceImage);
          }
        } else {
          setConvertedSourceImage(sourceImage);
        }

        if (isS3PresignedUrl(sampleImage)) {
          try {
            const base64Sample = await getS3ImageAsBase64(sampleImage);
            setConvertedSampleImage(base64Sample);
          } catch (error) {
            console.error('Failed to convert sampleImage from S3:', error);
            setConvertedSampleImage(sampleImage);
          }
        } else {
          setConvertedSampleImage(sampleImage);
        }

        setImagesReady(true);
      } catch (error) {
        console.error('Error converting images:', error);
        setConvertedSourceImage(sourceImage);
        setConvertedSampleImage(sampleImage);
        setImagesReady(true);
      }
    };

    convertImages();
  }, [sampleImage, sourceImage, isS3PresignedUrl]);

  useEffect(() => {
    if (!sampleImage || !sourceImage) {
      setImagesLoaded(false);
      return;
    }

    const sourceImg = sourceImgRef.current;
    const sampleImg = sampleImgRef.current;

    if (!sourceImg || !sampleImg) {
      return;
    }

    let sourceLoaded = false;
    let sampleLoaded = false;

    const checkBothLoaded = () => {
      if (
        sourceLoaded &&
        sampleLoaded &&
        sourceImg.naturalWidth > 0 &&
        sourceImg.naturalHeight > 0 &&
        sampleImg.naturalWidth > 0 &&
        sampleImg.naturalHeight > 0
      ) {
        setImagesLoaded(true);
      }
    };

    const handleSourceLoad = () => {
      sourceLoaded = true;
      if (sourceImg.naturalWidth > 0 && sourceImg.naturalHeight > 0) {
        sourceImg.style.width = 'auto';
        sourceImg.style.height = 'auto';
        sourceImg.style.maxWidth = '100%';
      }
      checkBothLoaded();
    };

    const handleSampleLoad = () => {
      sampleLoaded = true;
      if (sampleImg.naturalWidth > 0 && sampleImg.naturalHeight > 0) {
        sampleImg.style.width = 'auto';
        sampleImg.style.height = 'auto';
        sampleImg.style.maxWidth = '100%';
      }
      checkBothLoaded();
    };

    setImagesLoaded(false);
    sourceLoaded = false;
    sampleLoaded = false;

    if (sourceImg.complete && sourceImg.naturalWidth > 0) {
      sourceLoaded = true;
      sourceImg.style.width = 'auto';
      sourceImg.style.height = 'auto';
      sourceImg.style.maxWidth = '100%';
    } else {
      sourceImg.addEventListener('load', handleSourceLoad);
      sourceImg.addEventListener('error', handleSourceLoad);
    }

    if (sampleImg.complete && sampleImg.naturalWidth > 0) {
      sampleLoaded = true;
      sampleImg.style.width = 'auto';
      sampleImg.style.height = 'auto';
      sampleImg.style.maxWidth = '100%';
    } else {
      sampleImg.addEventListener('load', handleSampleLoad);
      sampleImg.addEventListener('error', handleSampleLoad);
    }

    checkBothLoaded();

    return () => {
      if (sourceImg) {
        sourceImg.removeEventListener('load', handleSourceLoad);
        sourceImg.removeEventListener('error', handleSourceLoad);
      }
      if (sampleImg) {
        sampleImg.removeEventListener('load', handleSampleLoad);
        sampleImg.removeEventListener('error', handleSampleLoad);
      }
    };
  }, [sampleImage, sourceImage]);

  const displaySourceImage = useMemo(() => {
    return convertedSourceImage ?? getImageSrc(sourceImage);
  }, [convertedSourceImage, sourceImage, getImageSrc]);

  const displaySampleImage = useMemo(() => {
    return convertedSampleImage ?? getImageSrc(sampleImage);
  }, [convertedSampleImage, sampleImage, getImageSrc]);

  const sourceIsBase64 = useMemo(() => convertedSourceImage?.startsWith('data:'), [convertedSourceImage]);
  const sampleIsBase64 = useMemo(() => convertedSampleImage?.startsWith('data:'), [convertedSampleImage]);

  const imageStyle = useMemo(
    () => ({
      maxWidth: '100%',
      height: 'auto',
      width: 'auto',
      display: 'block',
    }),
    [],
  );

  const buttonStyle = useMemo(
    () => ({
      top: 0,
      left: 0,
      position: 'absolute',
      padding: 0,
      border: 'none',
      background: 'transparent',
      cursor: imagesLoaded ? 'pointer' : 'default',
    }),
    [imagesLoaded],
  );

  const handleEditClick = useCallback(() => {
    if (imagesReady) {
      showMarkerArea('edit');
    }
  }, [imagesReady, showMarkerArea]);

  return (
    sampleImage &&
    sourceImage &&
    imagesReady &&
    displaySourceImage &&
    displaySampleImage && (
      <div className="marker-container" ref={parentRef}>
        <img
          id="sourceImage"
          ref={sourceImgRef}
          src={displaySourceImage}
          className="img-fluid"
          crossOrigin={sourceIsBase64 ? undefined : 'anonymous'}
          alt="sourceImage"
          style={imageStyle}
        />
        <button type="button" style={buttonStyle} onClick={handleEditClick} aria-label="Edit annotation">
          <img
            id="sampleImage"
            ref={sampleImgRef}
            src={displaySampleImage}
            style={imageStyle}
            crossOrigin={sampleIsBase64 ? undefined : 'anonymous'}
            className="img-fluid"
            alt="sampleImage"
          />
        </button>
      </div>
    )
  );
}

Annotation.propTypes = {
  sampleImage: PropTypes.string,
  sourceImage: PropTypes.string,
  markerObj: PropTypes.shape({}),
  onSaveAnnotation: PropTypes.func.isRequired,
  setIsEditOpen: PropTypes.func.isRequired,
  openType: PropTypes.string,
};

Annotation.defaultProps = {
  sampleImage: null,
  sourceImage: null,
  markerObj: {},
  openType: '',
};

export default Annotation;
